import React from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  PlayArrow as RunningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

/**
 * Component to display task execution status in the chat area.
 *
 * Props:
 * - status: Status object from useTaskStatus hook
 * - onCancel: Function to call when cancel button is clicked
 * - onDismiss: Function to call when dismiss button is clicked (for completed/failed/cancelled)
 * - themeMode: 'light' or 'dark'
 */
const TaskStatusIndicator = ({ status, onCancel, onDismiss, themeMode = 'dark' }) => {
  if (!status) return null;

  const isDark = themeMode === 'dark';

  const colors = isDark ? {
    background: '#27272a',
    border: '#3f3f46',
    textPrimary: '#ffffff',
    textSecondary: '#a1a1aa',
    success: '#22c55e',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6',
  } : {
    background: '#f4f4f5',
    border: '#d4d4d8',
    textPrimary: '#18181b',
    textSecondary: '#52525b',
    success: '#16a34a',
    error: '#dc2626',
    warning: '#d97706',
    info: '#2563eb',
  };

  const getStatusIcon = () => {
    switch (status.status) {
      case 'COMPLETED':
        return <CheckIcon sx={{ color: colors.success, fontSize: 20 }} />;
      case 'FAILED':
        return <ErrorIcon sx={{ color: colors.error, fontSize: 20 }} />;
      case 'CANCELLED':
        return <CancelIcon sx={{ color: colors.warning, fontSize: 20 }} />;
      case 'PENDING':
        return <PendingIcon sx={{ color: colors.info, fontSize: 20 }} />;
      case 'RUNNING':
      default:
        return <RunningIcon sx={{ color: colors.info, fontSize: 20, animation: 'pulse 1.5s infinite' }} />;
    }
  };

  const getStatusColor = () => {
    switch (status.status) {
      case 'COMPLETED':
        return colors.success;
      case 'FAILED':
        return colors.error;
      case 'CANCELLED':
        return colors.warning;
      default:
        return colors.info;
    }
  };

  const isTerminal = ['COMPLETED', 'FAILED', 'CANCELLED'].includes(status.status);
  const isRunning = status.status === 'RUNNING' || status.status === 'PENDING';
  const progressPercent = status.progressTotal > 0
    ? Math.min(100, Math.round((status.progressCurrent / status.progressTotal) * 100))
    : 0;

  return (
    <Collapse in={true}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mx: 2,
          mb: 2,
          backgroundColor: colors.background,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          borderLeft: `3px solid ${getStatusColor()}`,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon()}
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
              {status.scriptName || 'Script Execution'}
            </Typography>
            <Chip
              label={status.status}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: `${getStatusColor()}20`,
                color: getStatusColor(),
                fontWeight: 500,
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isRunning && onCancel && (
              <Tooltip title="Cancel execution">
                <IconButton
                  size="small"
                  onClick={onCancel}
                  sx={{
                    color: colors.error,
                    '&:hover': { backgroundColor: `${colors.error}20` },
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isTerminal && onDismiss && (
              <Tooltip title="Dismiss">
                <IconButton
                  size="small"
                  onClick={onDismiss}
                  sx={{
                    color: colors.textSecondary,
                    '&:hover': { backgroundColor: `${colors.textSecondary}20` },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Status Message */}
        {status.message && (
          <Typography
            variant="body2"
            sx={{
              color: colors.textSecondary,
              mb: isRunning && status.progressTotal > 0 ? 1 : 0,
              fontSize: '0.85rem',
            }}
          >
            {status.message}
          </Typography>
        )}

        {/* Progress Bar */}
        {isRunning && status.progressTotal > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                backgroundColor: `${colors.info}20`,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: colors.info,
                  borderRadius: 3,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: colors.textSecondary,
                minWidth: 40,
                textAlign: 'right',
              }}
            >
              {progressPercent}%
            </Typography>
          </Box>
        )}

        {/* Indeterminate progress for running without progress info */}
        {isRunning && !status.progressTotal && (
          <LinearProgress
            variant="indeterminate"
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: `${colors.info}20`,
              '& .MuiLinearProgress-bar': {
                backgroundColor: colors.info,
              },
            }}
          />
        )}

        {/* Timestamp */}
        {status.timestamp && (
          <Typography
            variant="caption"
            sx={{
              color: colors.textSecondary,
              display: 'block',
              mt: 0.5,
              opacity: 0.7,
              fontSize: '0.7rem',
            }}
          >
            {new Date(status.timestamp).toLocaleTimeString()}
          </Typography>
        )}
      </Paper>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </Collapse>
  );
};

export default TaskStatusIndicator;
